import React from "react";

import { parseRecipients } from "./schedule.utils";

const INPUT =
  "h-9 min-w-0 flex-1 rounded-lg border border-[#DCE2EA] bg-white px-3 text-[13.5px] text-[#1F2433] outline-none focus:border-[#6B4EFF]";

export type CopiesState = {
  cc: string;
  bcc: string;
  showCc: boolean;
  showBcc: boolean;
};
export const NO_COPIES: CopiesState = {
  cc: "",
  bcc: "",
  showCc: false,
  showBcc: false,
};
export const copiesFrom = (
  cc?: string[] | null,
  bcc?: string[] | null,
): CopiesState => ({
  cc: (cc || []).join(", "),
  bcc: (bcc || []).join(", "),
  showCc: !!cc?.length,
  showBcc: !!bcc?.length,
});

/** The address chips under a To / Cc / Bcc box. */
export const AddressChips = ({
  text,
  hint,
}: {
  text: string;
  hint?: string;
}) => {
  const { addresses } = parseRecipients(text);
  if (!addresses.length && !hint) return null;
  return (
    <div
      className={
        "mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-[#7A7F8C]"
      }>
      {addresses.length > 0
        ? addresses.map((a) => (
            <span
              key={a}
              className={
                "rounded-full border border-[#DCD3FF] bg-[#F4F1FF] px-2 py-0.5 font-semibold text-[#4B32D6]"
              }>
              {a}
            </span>
          ))
        : hint}
    </div>
  );
};

/** The small "Cc" / "Bcc" links (like mail apps). */
export const CopiesLinks = ({
  value,
  onChange,
}: {
  value: CopiesState;
  onChange: (next: CopiesState) => void;
}) => (
  <span className={"inline-flex gap-2 text-[12.5px] font-semibold"}>
    {!value.showCc && (
      <button
        type={"button"}
        onClick={() => onChange({ ...value, showCc: true })}
        className={"text-[#6B4EFF] hover:underline"}
        data-testid={"show-cc"}>
        Cc
      </button>
    )}
    {!value.showBcc && (
      <button
        type={"button"}
        onClick={() => onChange({ ...value, showBcc: true })}
        className={"text-[#6B4EFF] hover:underline"}
        data-testid={"show-bcc"}>
        Bcc
      </button>
    )}
  </span>
);

/** The opened Cc / Bcc boxes, each with its address chips (same look as To). */
export const CopiesFields = ({
  value,
  onChange,
  idPrefix,
}: {
  value: CopiesState;
  onChange: (next: CopiesState) => void;
  idPrefix: string;
}) => (
  <>
    {(
      [
        ["cc", "showCc", "Cc"],
        ["bcc", "showBcc", "Bcc"],
      ] as const
    ).map(
      ([key, show, label]) =>
        value[show] && (
          <div key={key} data-testid={`${idPrefix}-${key}-field`}>
            <div className={"flex items-center gap-2"}>
              <label
                htmlFor={`${idPrefix}-${key}`}
                className={
                  "w-9 shrink-0 text-[12.5px] font-bold text-[#3A4256]"
                }>
                {label}
              </label>
              <input
                id={`${idPrefix}-${key}`}
                value={value[key]}
                onChange={(e) => onChange({ ...value, [key]: e.target.value })}
                placeholder={
                  key === "bcc"
                    ? "Hidden copy: the others don't see these"
                    : "name@firm.com"
                }
                className={INPUT}
              />
              <button
                type={"button"}
                aria-label={`Remove ${label}`}
                onClick={() => onChange({ ...value, [key]: "", [show]: false })}
                className={
                  "rounded-lg px-2 py-1 text-[15px] leading-none text-[#9097A8] hover:bg-[#F1F2F5]"
                }>
                ×
              </button>
            </div>
            <div className={"pl-11"}>
              <AddressChips text={value[key]} />
            </div>
          </div>
        ),
    )}
  </>
);
